import { useFetchCampaignPledgeFetcher } from '@/campaigns/campaignsQueries'
import { useFetchCampaignsList } from '@/campaigns/campaignsQueries'
import { CampaignT } from '@/common/types'
import { useListHoldings } from '@/trading/tradingQueries'
import useAccountStore from '@/account/accountStore'
import { useEffect, useState } from 'react'

type Pledges = {
    [key: string]: number
}

export type CampaignsWithHoldings = CampaignT & {
    pledges: Pledges
    pledgedCompanies: CampaignT['targetCompaniesMetadata']
}

export const useCampaignsWithHoldings = () => {
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const { data: holdingsData, isLoading: isLoadingHoldings } = useListHoldings(activeAccountId)
    const { data: campaignsList, isLoading: isLoadingCampaigns } = useFetchCampaignsList()
    const [campaignsWithHoldings, setCampaignsWithHoldings] = useState<CampaignsWithHoldings[] | []>([])
    const [isLoadingCampaignsWithHoldings, setIsLoadingCampaignsWithHoldings] = useState(false)
    const fetchPledge = useFetchCampaignPledgeFetcher()

    useEffect(() => {
        setIsLoadingCampaignsWithHoldings(true)

        const fetchCampaignsWithHoldings = async () => {
            const results = await Promise.all(
                campaignsList?.campaigns?.map(async (campaign) => {
                    const holdings = holdingsData?.holdings.filter((holding) => {
                        return campaign.targetCompaniesAsxCodes.includes(holding.securityCode)
                    })

                    if (!holdings || holdings.length === 0) {
                        return null
                    }

                    const pledges: Pledges = {}
                    await Promise.all(
                        campaign.targetCompaniesAsxCodes.map(async (code) => {
                            try {
                                pledges[`${code}`] = await fetchPledge(campaign.campaignId, code)
                            } catch {
                                pledges[`${code}`] = 0
                            }
                        }),
                    )

                    const flattenedHoldings = holdings.map((holding) => holding.securityCode)

                    const pledgedCompanies = campaign.targetCompaniesMetadata?.filter((company) =>
                        flattenedHoldings.includes(company.code),
                    )

                    return { ...campaign, pledges, pledgedCompanies }
                }) ?? [],
            ).then((results) => results.filter((result) => result !== null))

            // @ts-ignore
            setCampaignsWithHoldings(results)
            setIsLoadingCampaignsWithHoldings(false)
        }

        if (!isLoadingHoldings && !isLoadingCampaigns) {
            fetchCampaignsWithHoldings()
        }
    }, [campaignsList, holdingsData, isLoadingHoldings, isLoadingCampaigns, fetchPledge])

    return { campaignsWithHoldings, isLoading: isLoadingCampaignsWithHoldings }
}
